import API_BASE_URL from "../config";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const authHeadersNoContentType = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const handle = async (res) => {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Backend error responses are JSON ({ error: "..." }) — surface just the
    // message text instead of the raw JSON blob when possible.
    let message = text || `Request failed with status ${res.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch {
      // not JSON — keep the raw text
    }
    throw new Error(message);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

// Returns { content, totalElements, totalPages, page, size } — pagination
// is server-side so the table only ever loads one page's worth of rows.
export const list = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.stage) qs.set("stage", params.stage);
  if (params.result) qs.set("result", params.result);
  if (params.search) qs.set("search", params.search);
  if (params.stageOutcome) qs.set("stageOutcome", params.stageOutcome);
  if (params.positionApplied) qs.set("positionApplied", params.positionApplied);
  if (params.qualification) qs.set("qualification", params.qualification);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  qs.set("page", params.page ?? 0);
  qs.set("size", params.size ?? 50);
  return fetch(`${API_BASE_URL}/api/placements?${qs}`, { headers: authHeaders() }).then(handle);
};

export const getById = (id) =>
  fetch(`${API_BASE_URL}/api/placements/${id}`, { headers: authHeaders() }).then(handle);

// "My Interviews" — every Test/Interview round this employee has been
// assigned to conduct, flat list (not nested under candidates).
export const getMyAssignments = (employeeId) =>
  fetch(`${API_BASE_URL}/api/placements/interviewer/${employeeId}`, { headers: authHeaders() }).then(handle);

export const create = (fields, resumeFile) => {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v ?? ""));
  if (resumeFile) form.append("resume", resumeFile);
  return fetch(`${API_BASE_URL}/api/placements`, {
    method: "POST",
    headers: authHeadersNoContentType(),
    body: form,
  }).then(handle);
};

export const update = (id, payload) =>
  fetch(`${API_BASE_URL}/api/placements/${id}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

export const replaceResume = (id, resumeFile) => {
  const form = new FormData();
  form.append("resume", resumeFile);
  return fetch(`${API_BASE_URL}/api/placements/${id}/resume`, {
    method: "POST",
    headers: authHeadersNoContentType(),
    body: form,
  }).then(handle);
};

// Fetches the resume as a Blob (auth header can't be attached to a plain
// <iframe src>, so the caller turns this into an object URL and embeds that
// in an in-app viewer instead of a bare `window.open` new tab).
export const fetchResumeBlob = async (id) => {
  const res = await fetch(`${API_BASE_URL}/api/placements/${id}/resume`, { headers: authHeadersNoContentType() });
  if (!res.ok) throw new Error("Resume not found");
  return res.blob();
};

export const addStage = (id, payload) =>
  fetch(`${API_BASE_URL}/api/placements/${id}/stage`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

export const updateStageEntry = (candidateId, historyId, payload) =>
  fetch(`${API_BASE_URL}/api/placements/${candidateId}/stage/${historyId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

export const getCompanyName = () =>
  fetch(`${API_BASE_URL}/api/company-info/name`, { headers: authHeaders() }).then(handle);

// Lightweight active-employee list (id/name/designation, no sensitive
// fields) — used to populate the "who's conducting this" dropdown when
// scheduling a Test/Interview, instead of a free-typed name.
export const getActiveEmployees = () =>
  fetch(`${API_BASE_URL}/api/employees/active/summary`, { headers: authHeaders() }).then(handle);

// Education / Position masters — power the Qualification and Position
// Applied dropdowns on the candidate form. Reuses the same simple
// name-only master pattern as /api/designations (plain-text responses on
// write, so these don't go through the JSON-parsing `handle` helper).
const handleTextResponse = async (res) => {
  const text = await res.text();
  if (!res.ok) throw new Error(text || "Request failed");
  return text;
};

export const getEducations = () =>
  fetch(`${API_BASE_URL}/api/educations/all`, { headers: authHeaders() }).then(handle);

export const addEducation = (name) =>
  fetch(`${API_BASE_URL}/api/educations/add`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify({ name }),
  }).then(handleTextResponse);

export const updateEducation = (id, name) =>
  fetch(`${API_BASE_URL}/api/educations/update/${id}`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ name }),
  }).then(handleTextResponse);

export const deleteEducation = (id) =>
  fetch(`${API_BASE_URL}/api/educations/delete/${id}`, {
    method: "DELETE", headers: authHeaders(),
  }).then(handleTextResponse);

export const getPositions = () =>
  fetch(`${API_BASE_URL}/api/positions/all`, { headers: authHeaders() }).then(handle);

export const addPosition = (name) =>
  fetch(`${API_BASE_URL}/api/positions/add`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify({ name }),
  }).then(handleTextResponse);

export const updatePosition = (id, name) =>
  fetch(`${API_BASE_URL}/api/positions/update/${id}`, {
    method: "PUT", headers: authHeaders(), body: JSON.stringify({ name }),
  }).then(handleTextResponse);

export const deletePosition = (id) =>
  fetch(`${API_BASE_URL}/api/positions/delete/${id}`, {
    method: "DELETE", headers: authHeaders(),
  }).then(handleTextResponse);

export const sendStageEmail = (candidateId, historyId, payload) =>
  fetch(`${API_BASE_URL}/api/placements/${candidateId}/stage/${historyId}/email`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

export const setResult = (id, payload) =>
  fetch(`${API_BASE_URL}/api/placements/${id}/result`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

export const exportExcel = async (params = {}) => {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.stage) qs.set("stage", params.stage);
  if (params.result) qs.set("result", params.result);
  if (params.stageOutcome) qs.set("stageOutcome", params.stageOutcome);
  if (params.positionApplied) qs.set("positionApplied", params.positionApplied);
  if (params.qualification) qs.set("qualification", params.qualification);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);

  const res = await fetch(`${API_BASE_URL}/api/placements/report?${qs}`, {
    headers: authHeadersNoContentType(),
  });
  if (!res.ok) throw new Error("Failed to download report");

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  const hasFilters = Boolean(params.search) || Boolean(params.stage) || Boolean(params.result) || Boolean(params.stageOutcome)
    || Boolean(params.positionApplied) || Boolean(params.qualification) || params.from || params.to;
  a.href = url;
  a.download = hasFilters ? `Candidates_Filtered_${date}.xlsx` : `Candidates_${date}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const remove = (id) =>
  fetch(`${API_BASE_URL}/api/placements/${id}`, {
    method: "DELETE",
    headers: authHeadersNoContentType(),
  }).then((res) => {
    if (!res.ok) throw new Error("Failed to delete candidate");
  });
